<template>
    <Teleport to="body">
        <Transition name="help-drawer">
            <div v-if="helpStore.isOpen" class="help-drawer-root" @keydown="onDrawerKeydown">
                <aside ref="drawerEl" class="help-drawer" role="dialog" aria-label="Help" tabindex="-1">
                    <header class="help-drawer-header">
                        <h2 class="help-drawer-title">{{ headerTitle }}</h2>
                        <button type="button" class="btn-close" aria-label="Close help" @click="helpStore.close()" />
                    </header>

                    <div class="help-drawer-search">
                        <input
                            ref="searchInputEl"
                            v-model="searchInput"
                            type="search"
                            class="form-control form-control-sm"
                            placeholder="Search help…"
                            @input="onSearchInput"
                        />
                    </div>

                    <div class="help-drawer-body">
                        <div v-if="helpStore.loading" class="text-muted">Loading…</div>

                        <template v-else-if="showingSearchResults">
                            <div v-if="helpStore.indexLoading || !helpStore.indexLoaded" class="text-muted">
                                Loading…
                            </div>
                            <p v-else-if="helpStore.searchResults.length === 0" class="text-muted">
                                No results for "{{ helpStore.searchQuery }}".
                            </p>
                            <ul v-else class="help-doc-list">
                                <li v-for="doc in helpStore.searchResults" :key="doc.key">
                                    <a href="#" @click.prevent="openKey(doc.key)">{{ doc.title }}</a>
                                </li>
                            </ul>
                        </template>

                        <template v-else-if="helpStore.currentDoc">
                            <article class="help-doc" v-html="renderedBody" @click="onBodyClick" />
                            <section v-if="relatedDocs.length > 0" class="help-related">
                                <h3>Related</h3>
                                <ul>
                                    <li v-for="doc in relatedDocs" :key="doc.key">
                                        <a href="#" @click.prevent="openKey(doc.key)">{{ doc.title }}</a>
                                    </li>
                                </ul>
                            </section>
                        </template>

                        <template v-else>
                            <div v-if="helpStore.indexLoading || !helpStore.indexLoaded" class="text-muted">
                                Loading…
                            </div>
                            <template v-else>
                                <p class="text-muted">
                                    No help authored for this screen yet. Browse the available topics below:
                                </p>
                                <ul class="help-doc-list">
                                    <li v-for="doc in helpStore.index" :key="doc.key">
                                        <a href="#" @click.prevent="openKey(doc.key)">{{ doc.title }}</a>
                                    </li>
                                </ul>
                            </template>
                        </template>
                    </div>
                </aside>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useHelpStore } from '@/stores/helpStore';

const helpStore = useHelpStore();
const searchInput = ref('');
const drawerEl = ref<HTMLElement | null>(null);
const searchInputEl = ref<HTMLInputElement | null>(null);
let previouslyFocused: HTMLElement | null = null;

const FOCUSABLE_SELECTOR =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

watch(
    () => helpStore.isOpen,
    (open) => {
        if (open) {
            previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            void nextTick(() => {
                searchInputEl.value?.focus();
            });
        } else {
            searchInput.value = '';
            previouslyFocused?.focus?.();
            previouslyFocused = null;
        }
    },
);

function getFocusableElements(): HTMLElement[] {
    if (!drawerEl.value) {
        return [];
    }
    return Array.from(drawerEl.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled') && el.offsetParent !== null,
    );
}

function onDrawerKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        helpStore.close();
        return;
    }

    if (event.key !== 'Tab') {
        return;
    }

    const focusable = getFocusableElements();
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) {
        event.preventDefault();
        drawerEl.value?.focus();
        return;
    }

    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    if (event.shiftKey) {
        if (active === first || !drawerEl.value?.contains(active)) {
            event.preventDefault();
            last.focus();
        }
    } else if (active === last) {
        event.preventDefault();
        first.focus();
    }
}

const showingSearchResults = computed(() => helpStore.searchQuery.trim().length > 0);

const headerTitle = computed(() => {
    if (showingSearchResults.value) {
        return 'Search';
    }
    return helpStore.currentDoc?.frontmatter.title || 'Help';
});

const renderedBody = computed(() => {
    const body = helpStore.currentDoc?.body ?? '';
    const html = marked.parse(body, { async: false });
    const withRewrittenLinks = html.replace(
        /href="help:([^"]+)"/g,
        (_m, key: string) => `href="#" data-help-key="${key}"`,
    );
    return DOMPurify.sanitize(withRewrittenLinks, { ADD_ATTR: ['data-help-key'] });
});

const relatedDocs = computed(() => {
    const related = helpStore.currentDoc?.frontmatter.related ?? [];
    return related
        .map((key) => helpStore.index.find((d) => d.key === key))
        .filter((d): d is NonNullable<typeof d> => Boolean(d));
});

function onSearchInput() {
    helpStore.setSearchQuery(searchInput.value);
}

function openKey(key: string) {
    searchInput.value = '';
    helpStore.setSearchQuery('');
    void helpStore.loadDoc(key);
}

function onBodyClick(event: MouseEvent) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
        return;
    }
    const anchor = target.closest<HTMLAnchorElement>('a[data-help-key]');
    if (!anchor) {
        return;
    }
    event.preventDefault();
    const key = anchor.getAttribute('data-help-key');
    if (key) {
        openKey(key);
    }
}
</script>

<style scoped lang="scss">
.help-drawer-root {
    position: fixed;
    inset: 0;
    z-index: 1090;
    display: flex;
    justify-content: flex-end;
    pointer-events: none;
}

.help-drawer {
    position: relative;
    pointer-events: auto;
    width: min(420px, 100vw);
    height: 100%;
    background: var(--bs-body-bg, #fff);
    color: var(--bs-body-color, #212529);
    box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.help-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--bs-border-color, #dee2e6);
}

.help-drawer-title {
    font-size: 1rem;
    font-weight: 600;
    margin: 0;
}

.help-drawer-search {
    padding: 0.5rem 1rem;
    border-bottom: 1px solid var(--bs-border-color, #dee2e6);
}

.help-drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
}

.help-doc {
    line-height: 1.5;

    :deep(h1) {
        font-size: 1.25rem;
        margin-top: 0;
    }

    :deep(h2) {
        font-size: 1.05rem;
        margin-top: 1.25rem;
    }

    :deep(h3) {
        font-size: 0.95rem;
        margin-top: 1rem;
    }

    :deep(code) {
        background: var(--bs-secondary-bg, #f1f3f5);
        padding: 0.1em 0.3em;
        border-radius: 3px;
    }
}

.help-doc-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        padding: 0.3rem 0;
    }
}

.help-related {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid var(--bs-border-color, #dee2e6);

    h3 {
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: var(--bs-secondary-color, #6c757d);
    }

    ul {
        list-style: none;
        padding: 0;
    }
}

.help-drawer-enter-active,
.help-drawer-leave-active {
    transition: opacity 0.2s ease;

    .help-drawer {
        transition: transform 0.2s ease;
    }
}

.help-drawer-enter-from,
.help-drawer-leave-to {
    opacity: 0;

    .help-drawer {
        transform: translateX(100%);
    }
}
</style>
